import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import ProductCard from '../components/product/ProductCard';
import { expandProductCards } from '../utils/productCards';
import { getProducts, getCategories, getPopularProducts, contentAPI } from '../services/api';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { useDataRefresh } from '../context/DataRefreshContext';
import { Product, Category } from '../types';
import './Landing.css';

// Inline SVG icon components
const SvgIcon: React.FC<{ size?: number; children: React.ReactNode }> = ({ size = 24, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const IconTruck: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></SvgIcon>
);
const IconDollar: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></SvgIcon>
);
const IconShield: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></SvgIcon>
);
const IconTag: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></SvgIcon>
);
const IconShoppingBag: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></SvgIcon>
);
const IconPackage: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></SvgIcon>
);
const IconGift: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></SvgIcon>
);
const IconCreditCard: React.FC<{ size?: number }> = ({ size }) => (
  <SvgIcon size={size}><rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" /></SvgIcon>
);

const ICON_COMPONENTS: { [key: string]: React.ReactNode } = {
  'truck': <IconTruck size={32} />,
  'dollar-sign': <IconDollar size={32} />,
  'shield-check': <IconShield size={32} />,
  'tag': <IconTag size={32} />,
  'shopping-bag': <IconShoppingBag size={32} />,
  'package': <IconPackage size={32} />,
  'gift': <IconGift size={32} />,
  'credit-card': <IconCreditCard size={32} />,
};

const Landing: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { refreshTrigger } = useDataRefresh();
  const [categories, setCategories] = useState<Category[]>([]);
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<{ [key: number]: Product[] }>({});
  const [loading, setLoading] = useState(true);
  const [homeContent, setHomeContent] = useState<any>(null);

  // Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const [banners, setBanners] = useState<any[]>([]);
  const [bannersLoading, setBannersLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      setBannersLoading(true);
      try {
        const response = await contentAPI.getBanners();
        const data = response.data || response;
        const bannerList = Array.isArray(data) ? data : (data.results || []);
        setBanners(bannerList);

        // Preload all banner images before showing the section
        if (bannerList.length > 0) {
          await Promise.all(
            bannerList.map(
              (b: any) =>
                new Promise<void>((resolve) => {
                  if (!b.image) { resolve(); return; }
                  const img = new Image();
                  img.onload = () => resolve();
                  img.onerror = () => resolve();
                  img.src = b.image;
                })
            )
          );
        }
      } catch (error) {
        console.error('Failed to fetch banners:', error);
      } finally {
        setBannersLoading(false);
      }
    };
    fetchBanners();
  }, [refreshTrigger.banners]);

  const totalSlides = banners.length;

  useEffect(() => {
    fetchData();
    // Track Landing page view
    trackFacebookEvent('ViewContent', {
      content_name: 'Landing Page',
      content_type: 'page'
    });
  }, [refreshTrigger.categories]); // ✅ Re-fetch when categories are updated

  // Auto-slide effect — only run when there are banners
  useEffect(() => {
    if (totalSlides <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 5000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const categoriesRes = await getCategories();
      const cats = categoriesRes.slice(0, 4);
      setCategories(cats);

      const popularRes = await getPopularProducts();
      setPopularProducts(popularRes.slice(0, 6));

      const categoryProds: { [key: number]: Product[] } = {};
      for (const cat of cats) {
        const prodsRes = await getProducts({ category: cat.slug, page_size: 6 });
        categoryProds[cat.id] = prodsRes.results;
      }
      setCategoryProducts(categoryProds);

      const contentData = await contentAPI.getHomePageContent();
      setHomeContent(contentData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentLang = i18n.language;

  return (
    <div className="landing-page">
      {/* HERO BANNER — only rendered after banners load */}
      {bannersLoading ? (
        <section className="banner">
          <div className="container">
            <div className="slider-container">
              <div className="banner-skeleton-loader" />
            </div>
          </div>
        </section>
      ) : banners.length > 0 ? (
        <section className="banner">
          <div className="container">
            <div className="slider-container has-scrollbar">
              <div
                className="slider-wrapper"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {banners.map((banner, index) => (
                  <div 
                    key={banner.id} 
                    className="slider-item"
                    onClick={() => {
                      if (banner.link) {
                        if (banner.link.startsWith('http://') || banner.link.startsWith('https://')) {
                          window.open(banner.link, '_blank');
                        } else {
                          navigate(banner.link);
                        }
                      }
                    }}
                    style={{ cursor: banner.link ? 'pointer' : 'default' }}
                  >
                    <img
                      src={banner.image}
                      alt={i18n.language === 'bn' ? banner.title_bn : banner.title_en}
                      className="slider-image"
                    />
                  </div>
                ))}
              </div>

              {totalSlides > 1 && (
                <div className="slider-dots">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                      key={index}
                      className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                      onClick={() => goToSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* CATEGORY BAR */}
      {/* CATEGORY BAR (Temporarily Hidden)
      <section className="category">
        <div className="container">
          <div className="section-header">
            <h2 className="title">
              {currentLang === 'bn' ? '📂 ক্যাটাগরি' : '📂 Categories'}
            </h2>
          </div>
          <div className="category-item-container has-scrollbar">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="category-item"
              >
                <div className="category-img-box">
                  <span className="category-icon">
                    {category.name_en?.charAt(0) || '📦'}
                  </span>
                </div>
                <div className="category-content-box">
                  <h3 className="category-item-title">
                    {currentLang === 'bn' ? category.name_bn : category.name_en}
                  </h3>
                </div>
              </Link>
            ))}

            <Link to="/products" className="category-item view-all">
              <div className="category-img-box">
                <span className="category-icon">→</span>
              </div>
              <div className="category-content-box">
                <h3 className="category-item-title">
                  {currentLang === 'bn' ? 'সব দেখুন' : 'View All'}
                </h3>
              </div>
            </Link>
          </div>
        </div>
      </section>
      */}

      {/* MAIN CONTENT */}
      <main className="main-content">
        <div className="container">

          {/* Popular Products Section */}
          <section className="product-section">
            <div className="section-header">
              <h2 className="title">
                {currentLang === 'bn' ? '🔥 জনপ্রিয় পণ্য' : '🔥 Popular Products'}
              </h2>
              <Link to="/products" className="view-all-btn">
                {currentLang === 'bn' ? 'সব দেখুন' : 'View All'} →
              </Link>
            </div>

            {loading ? (
              <div className="products-grid">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton-card"></div>
                ))}
              </div>
            ) : (
              <div className="products-grid">
                {popularProducts.slice(0, 6).flatMap(expandProductCards).map((item) => (
                  <ProductCard key={`${item.product.id}-${item.color || 'default'}`} product={item.product} color={item.color} colorImage={item.colorImage} />
                ))}
              </div>
            )}
          </section>

          {/* Category-wise Product Sections */}
          {categories.map((category) => (
            <section key={category.id} className="product-section">
              <div className="section-header">
                <h2 className="title">
                  {currentLang === 'bn' ? category.name_bn : category.name_en}
                </h2>
                <Link to={`/products?category=${category.id}`} className="view-all-btn">
                  {currentLang === 'bn' ? 'সব দেখুন' : 'View All'} →
                </Link>
              </div>

              {loading ? (
                <div className="products-grid">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="skeleton-card"></div>
                  ))}
                </div>
              ) : (
                <div className="products-grid">
                  {categoryProducts[category.id]?.flatMap(expandProductCards).map((item) => (
                    <ProductCard key={`${item.product.id}-${item.color || 'default'}`} product={item.product} color={item.color} colorImage={item.colorImage} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </main>

      {/* FEATURES SECTION */}
      <section className="features-section">
        <div className="container">
          <div className="features-grid">
            {homeContent?.features && homeContent.features.length > 0 ? (
              homeContent.features.map((feature: any, index: number) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    {ICON_COMPONENTS[feature.icon] || <IconTruck size={32} />}
                  </div>
                  <div className="feature-content">
                    <h3>{currentLang === 'bn' ? feature.title_bn : feature.title_en}</h3>
                    <p>{currentLang === 'bn' ? feature.subtitle_bn : feature.subtitle_en}</p>
                  </div>
                </div>
              ))
            ) : (
              // Fallback / Skeleton or Hide
              <>
                <div className="feature-card">
                  <div className="feature-icon"><IconTruck size={32} /></div>
                  <div className="feature-content">
                    <h3>{currentLang === 'bn' ? 'দ্রুত ডেলিভারি' : 'Fast Delivery'}</h3>
                    <p>{currentLang === 'bn' ? 'সারাদেশে ২-৩ দিনে' : '2-3 days nationwide'}</p>
                  </div>
                </div>
                <div className="feature-card">
                  <div className="feature-icon"><IconDollar size={32} /></div>
                  <div className="feature-content">
                    <h3>{currentLang === 'bn' ? 'ক্যাশ অন ডেলিভারি' : 'Cash on Delivery'}</h3>
                    <p>{currentLang === 'bn' ? 'হাতে পেয়ে পেমেন্ট' : 'Pay when delivered'}</p>
                  </div>
                </div>
                <div className="feature-card">
                  <div className="feature-icon"><IconShield size={32} /></div>
                  <div className="feature-content">
                    <h3>{currentLang === 'bn' ? 'নিরাপদ কেনাকাটা' : 'Secure Shopping'}</h3>
                    <p>{currentLang === 'bn' ? '১০০% নিরাপদ' : '100% safe & secure'}</p>
                  </div>
                </div>
                <div className="feature-card">
                  <div className="feature-icon"><IconTag size={32} /></div>
                  <div className="feature-content">
                    <h3>{currentLang === 'bn' ? 'বিশেষ অফার' : 'Special Offers'}</h3>
                    <p>{currentLang === 'bn' ? 'নিয়মিত ছাড়' : 'Regular discounts'}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
